import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { PRODUCT_NAME, COMPANY } from "@/config";
import { getRef } from "@/lib/ref";
import { PublicFooter } from "@/components/PublicFooter";
import { MockBrowserFrame } from "@/components/MockBrowserFrame";
import HeroAnimation from "@/components/HeroAnimation";
import { GeneratorMock, VeilleMock, StudioMock, CalendarMock } from "@/components/ProductMockups";
import { LinkedInIcon, FacebookIcon, InstagramIcon, XIcon, SubstackIcon, BRAND_COLORS } from "@/components/BrandIcons";
import {
  Sparkles, Radar, ImageIcon, CalendarDays, Check, ChevronDown, ArrowRight,
  Gift, ShieldCheck, Layers3, Share2, Quote,
} from "lucide-react";

const TESTIMONIALS = [
  { n: "Sophie M.", r: "Architecte · Membre APOLLO", c: "#E85C4A",
    q: "Avant Pulse, je savais qu'il fallait publier régulièrement, mais je n'avais jamais le temps. Aujourd'hui, je pars simplement d'une idée et Pulse s'occupe du reste. Je communique beaucoup plus, sans y passer mes soirées." },
  { n: "Nicolas D.", r: "Consultant en stratégie", c: "#2F6F6B",
    q: "J'ai testé pas mal d'outils IA, mais il fallait toujours jongler entre plusieurs solutions. Avec Pulse, je crée mon post, mon visuel et ses déclinaisons pour chaque réseau au même endroit. C'est simple, rapide et franchement efficace." },
  { n: "Émilie R.", r: "Agent immobilier", c: "#B0483F",
    q: "Le vrai déclic pour moi, ce sont les visuels. Pulse peut générer des images professionnelles en intégrant ma propre photo. Je peux enfin créer des publications qui attirent l'œil tout en restant cohérentes avec mon image personnelle." },
  { n: "Thomas L.", r: "Dirigeant d'entreprise · Membre APOLLO", c: "#3A5A82",
    q: "Pulse m'a permis de devenir beaucoup plus régulier dans ma communication. Je crée mes contenus en quelques minutes et je peux les diffuser sur plusieurs réseaux. Et l'avoir inclus dans mon adhésion APOLLO, c'est clairement un vrai plus." },
  { n: "Claire B.", r: "Coach professionnelle", c: "#8A5A3B",
    q: "Mon problème n'était pas de savoir que je devais communiquer, mais de trouver quoi dire et comment le dire. Pulse m'aide à trouver les idées, rédige les contenus et crée les visuels. Je gagne du temps sans perdre ma personnalité." },
  { n: "Julien P.", r: "Courtier en financement", c: "#5B5F97",
    q: "Avant, un post me prenait facilement une heure entre le texte, l'image et les adaptations. Avec Pulse, je peux décliner mon contenu pour LinkedIn, Instagram, Facebook, X ou Substack très rapidement. Pour moi, c'est surtout du temps récupéré." },
];

const initials = (name) => name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(n);

const PILLARS = [
  { icon: Sparkles, t: "Votre style appris", d: "Un onboarding éditorial et vos posts de référence : vos textes sonnent comme vous." },
  { icon: Layers3, t: "Déposez, partez, revenez", d: "L'Atelier travaille pendant que vous faites autre chose. Jusqu'à 5 demandes en attente." },
  { icon: ImageIcon, t: "Des visuels à votre image", d: "Des affiches de qualité professionnelle, avec votre visage si vous le souhaitez." },
  { icon: Share2, t: "Un post, cinq réseaux", d: "Une version adaptée pour Facebook, Instagram, X et Substack. Pas un copier-coller." },
  { icon: Radar, t: "Vos idées chaque lundi", d: "Votre veille (blogs, médias, YouTube) transformée en angles reliés à vos piliers." },
  { icon: CalendarDays, t: "Publication automatique", d: "Calendrier éditorial et publication LinkedIn via l'API officielle." },
];

const SECTIONS = [
  { title: "Déposez votre idée. Reprenez votre journée.", lines: ["Vous déposez jusqu'à 5 demandes, puis vous fermez Pulse.", "L'Atelier rédige et illustre pendant que vous travaillez.", "Vous revenez plus tard : vos propositions vous attendent, conservées 30 jours."], mock: <GeneratorMock />, url: "pulse.apollo-business.com/creer" },
  { title: "Ne cherchez plus jamais quoi poster", lines: ["Ajoutez vos sources : blogs, médias, chaînes YouTube.", "Chaque lundi, 3-4 angles reliés à vos piliers de contenu.", "Un bouton « Rédiger » et l'angle devient 3 propositions."], mock: <VeilleMock />, url: "pulse.apollo-business.com/veille", reverse: true },
  { title: "Des visuels que personne ne devine générés", lines: ["Des affiches éditoriales calées sur votre marque et vos couleurs.", "Avec votre visage, à partir de vos photos, si vous le souhaitez.", "Un contrôle orthographique automatique sur chaque texte incrusté."], mock: <StudioMock />, url: "pulse.apollo-business.com/studio" },
  { title: "Programmez le mois, vivez votre vie", lines: ["Planifiez vos posts sur tout le mois en quelques minutes.", "Publication automatique via l'API officielle LinkedIn.", "On vous prévient avant chaque échéance importante."], mock: <CalendarMock />, url: "pulse.apollo-business.com/calendrier", reverse: true },
];

const CHANNELS = [
  { n: "LinkedIn", k: "linkedin", I: LinkedInIcon, d: "Publication automatique, via l'API officielle." },
  { n: "Facebook", k: "facebook", I: FacebookIcon, d: "Une version conversationnelle, qui se comprend seule." },
  { n: "Instagram", k: "instagram", I: InstagramIcon, d: "Une légende rythmée et ses hashtags, prêts à coller." },
  { n: "X", k: "x", I: XIcon, d: "Une accroche courte qui renvoie vers votre post LinkedIn." },
  { n: "Substack", k: "substack", I: SubstackIcon, d: "Un vrai article : titre, chapô, intertitres, conclusion." },
];

const FAQ = [
  { q: "Est-ce conforme aux règles de LinkedIn ?", a: "Oui. Nous utilisons l'API officielle LinkedIn, avec votre autorisation explicite. Aucun robot d'engagement, aucun scraping : vous publiez vos propres contenus, validés par vous." },
  { q: "Pulse publie-t-il aussi sur Facebook, Instagram, X et Substack ?", a: "Pulse rédige la version adaptée à chacun de ces réseaux et vous donne le texte et l'image, prêts à publier. Mais il ne publie automatiquement que sur LinkedIn. C'est un choix : automatiser la publication sur les réseaux de Meta exige un compte professionnel et une autorisation qui n'est pas à la portée de tous. Depuis un téléphone, un bouton « Partager » envoie directement le texte et l'image vers l'application de votre choix." },
  { q: "Les versions pour les autres réseaux sont-elles de simples copies ?", a: "Non, et c'est tout l'intérêt. Pulse construit d'abord un message central à partir de votre post, puis en dérive une version propre à chaque plateforme : un article structuré pour Substack, une accroche courte pour X, une légende rythmée avec ses hashtags pour Instagram. Mêmes faits, même position, écriture différente." },
  { q: "Mes posts feront-ils « écrits par une IA » ?", a: "Non. Votre style est appris à partir de vos propres posts et de votre onboarding éditorial. Le résultat sonne comme vous, vous gardez toujours le dernier mot." },
  { q: "Que se passe-t-il à la fin de l'essai ?", a: "Votre compte passe en lecture seule. Tous vos posts et données sont conservés. Vous choisissez une formule quand vous le souhaitez pour reprendre la création." },
  { q: "Puis-je annuler quand je veux ?", a: "Oui, en libre-service depuis votre espace de facturation. Sans engagement, sans friction." },
  { q: "Où sont mes données ?", a: <>Vos données sont traitées par APOLLO et par des prestataires sous contrat, dont certains situés aux États-Unis (hébergement, rédaction des textes, création des visuels), dans le cadre prévu par le RGPD. Le détail est dans notre <Link to="/confidentialite" data-testid="faq-privacy-link" className="text-coral-dark font-semibold underline underline-offset-2">politique de confidentialité</Link>.</> },
  { q: "Pourquoi reconnecter LinkedIn tous les 60 jours ?", a: "C'est une règle de sécurité de LinkedIn. La reconnexion prend un clic et repart pour 60 jours, nous vous prévenons toujours à l'avance, par bannière et par e-mail." },
  { q: `Qui est derrière ${PRODUCT_NAME} ?`, a: `${PRODUCT_NAME} est édité par ${COMPANY}, un réseau d'affaires de dirigeants. L'outil est né de leurs propres besoins éditoriaux.` },
];

function Nav({ trialDays }) {
  const navigate = useNavigate();
  const [solid, setSolid] = useState(false);
  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header data-testid="public-nav" className={`fixed top-0 inset-x-0 z-50 transition-all ${solid ? "backdrop-blur-xl bg-white/80 border-b border-black/5 shadow-sm" : ""}`}>
      <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
        <span className="font-serif text-2xl font-extrabold text-ink">{PRODUCT_NAME}<span className="text-coral-strong">.</span></span>
        <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-600">
          <a href="#fonctionnalites" className="hover:text-coral-dark transition-colors">Fonctionnalités</a>
          <a href="#tarifs" className="hover:text-coral-dark transition-colors">Tarifs</a>
          <a href="#faq" className="hover:text-coral-dark transition-colors">FAQ</a>
        </nav>
        <div className="flex items-center gap-3">
          <button data-testid="nav-code-btn" onClick={() => navigate("/invitation")} className="hidden sm:inline text-sm font-semibold text-slate-600 hover:text-coral-dark transition-colors">J'ai un code</button>
          <button data-testid="nav-login-btn" onClick={() => navigate("/login")} className="text-sm font-semibold text-slate-600 hover:text-ink transition-colors">Se connecter</button>
          <button data-testid="nav-cta-btn" onClick={() => navigate("/login?register=1")} className="text-sm font-bold text-white bg-coral-strong px-4 py-2 rounded-full hover:bg-coral-hover active:scale-95 transition-all">Essayer gratuitement</button>
        </div>
      </div>
    </header>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [trialDays, setTrialDays] = useState(14);
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [referrer, setReferrer] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/config`).then((r) => { setTrialDays(r.data.trial_days); setPlans(r.data.plans); }).catch(() => {});
    const ref = getRef();
    if (ref) axios.get(`${API}/public/referrer/${ref}`).then((r) => setReferrer(r.data.name)).catch(() => {});
  }, []);

  const start = () => navigate("/login?register=1");
  const solo = plans.find((p) => p.key === "solo");

  return (
    <div className="min-h-screen bg-[#FBFAF8] text-ink font-sans">
      <Nav trialDays={trialDays} />

      {referrer && (
        <div data-testid="ref-banner" className="pt-16">
          <div className="bg-coral-strong text-white text-center text-sm py-2 font-semibold">
            👋 Recommandé par {referrer}, profitez de votre essai gratuit de {trialDays} jours.
          </div>
        </div>
      )}

      {/* Hero */}
      <section className={`max-w-6xl mx-auto px-6 ${referrer ? "pt-16" : "pt-32"} pb-16 text-center`}>
        <div className="fade-in-up">
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-coral-dark bg-coral-soft px-3 py-1 rounded-full mb-6"><ShieldCheck className="h-3.5 w-3.5" /> API officielle LinkedIn · 100 % votre voix</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.08] max-w-4xl mx-auto text-ink">Une idée. Votre style. Tous vos réseaux.</h1>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto mt-6 leading-relaxed">
            {PRODUCT_NAME} apprend votre façon d'écrire, rédige et illustre pendant que vous faites autre chose,
            publie sur LinkedIn à votre place, et décline le même message pour Facebook, Instagram, X et Substack.
            Vous gardez la voix, vous récupérez le temps.
          </p>
          <div className="flex items-center justify-center gap-5 mt-7 text-slate-400" data-testid="hero-channels">
            {CHANNELS.map((c) => (
              <c.I key={c.k} className="h-[22px] w-[22px] transition-colors hover:text-[color:var(--brand)]"
                style={{ "--brand": BRAND_COLORS[c.k] }} aria-label={c.n} />
            ))}
          </div>
          <div className="flex flex-col items-center gap-2 mt-8">
            <button data-testid="hero-cta-btn" onClick={start} className="flex items-center gap-2 text-base font-bold text-white bg-coral-strong px-7 py-3.5 rounded-full hover:bg-coral-hover active:scale-95 transition-all shadow-lg shadow-coral/20">
              Essayer gratuitement <ArrowRight className="h-4 w-4" />
            </button>
            <span className="text-xs text-slate-400">{trialDays} jours d'essai, sans carte bancaire</span>
          </div>
        </div>
        {/* Le parcours réel de l'Atelier, rejoué en boucle : on montre le produit, on ne le décrit pas. */}
        <div className="mt-14 max-w-4xl mx-auto fade-in-up" style={{ animationDelay: "0.15s" }}>
          <HeroAnimation />
        </div>
      </section>

      {/* Social proof */}
      <section className="border-y border-black/5 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-10 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-coral-dark mb-3">L'outil est né d'un besoin réel</p>
          <p className="text-lg text-ink leading-relaxed">
            {PRODUCT_NAME} est né au sein d'<span className="font-bold">{COMPANY}</span>, un réseau d'affaires de dirigeants,
            parce que ses membres n'avaient <span className="font-bold">ni le temps d'écrire, ni l'envie de sonner comme tout le monde</span>.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section id="fonctionnalites" className="max-w-6xl mx-auto px-6 py-24">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mb-14">Tout ce qu'il faut pour rayonner, sans y passer vos soirées</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => (
            <div key={i} className="rounded-2xl border border-black/5 bg-white p-6 hover:-translate-y-1 transition-transform shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="h-11 w-11 rounded-xl bg-coral-soft flex items-center justify-center mb-4"><p.icon className="h-5 w-5 text-coral-dark" /></div>
              <h3 className="font-bold text-ink mb-1.5">{p.t}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Multicanal */}
      <section className="bg-ink-dark text-white" data-testid="multicanal-section">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 text-coral mb-3">
              <Share2 className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em]">Multicanal</span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">Un post. Cinq réseaux. Aucun copier-coller.</h2>
            <p className="text-white/70 leading-relaxed">
              Le même message, mais écrit dans les codes de chaque plateforme. Un article de fond sur Substack,
              une accroche courte sur X, une légende rythmée sur Instagram. Et le même visuel partout.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CHANNELS.map((c) => (
              <div key={c.n} className="group rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-colors hover:bg-white/[0.07]">
                <div className="flex items-center gap-2.5 mb-2">
                  <c.I className="h-5 w-5 shrink-0 text-white/50 transition-colors group-hover:text-white" aria-label={c.n} />
                  <p className="font-bold">{c.n}</p>
                </div>
                <p className="text-sm text-white/60 leading-relaxed">{c.d}</p>
              </div>
            ))}
            <div className="rounded-2xl border border-coral/40 bg-coral-strong/15 p-5 flex items-center">
              <p className="text-sm text-white/85 leading-relaxed">
                <strong className="text-coral">Vous gardez la main.</strong> Pulse publie sur LinkedIn ;
                ailleurs, il vous donne le texte et l'image, prêts à publier en deux clics.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed sections */}
      <section className="max-w-6xl mx-auto px-6 pt-24 pb-8 space-y-24">
        {SECTIONS.map((s, i) => (
          <div key={i} className={`grid lg:grid-cols-2 gap-12 items-center ${s.reverse ? "lg:[direction:rtl]" : ""}`}>
            <div className="lg:[direction:ltr]">
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-ink mb-5">{s.title}</h2>
              <ul className="space-y-3">
                {s.lines.map((l, j) => (
                  <li key={j} className="flex items-start gap-3 text-slate-600"><Check className="h-5 w-5 text-coral-dark shrink-0 mt-0.5" /> {l}</li>
                ))}
              </ul>
            </div>
            <div className="lg:[direction:ltr]"><MockBrowserFrame url={s.url}>{s.mock}</MockBrowserFrame></div>
          </div>
        ))}
      </section>

      {/* Affiliation */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="rounded-3xl bg-ink text-white p-10 md:p-14 overflow-hidden relative">
          <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-coral-strong/20 blur-3xl" />
          <div className="relative max-w-2xl">
            <div className="flex items-center gap-2 text-coral mb-3"><Gift className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-[0.2em]">La recommandation dans l'ADN</span></div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">Recommandez {PRODUCT_NAME}, gagnez <span className="text-coral">50 %</span> la première année.</h2>
            <p className="text-white/70 leading-relaxed mb-6">
              Chez {COMPANY}, la croissance passe par la recommandation entre pairs. Chaque membre dispose d'un lien de parrainage personnel :
              toute personne qui arrive par votre lien voit « Recommandé par vous », et vous touchez 50 % la première année, 25 % ensuite, ou un crédit sur votre cotisation.
            </p>
            <button data-testid="affiliate-cta-btn" onClick={start} className="flex items-center gap-2 text-sm font-bold text-ink bg-white px-5 py-3 rounded-full hover:bg-coral-soft active:scale-95 transition-all">
              Devenir affilié <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Témoignages du groupe pilote. Pastilles à initiales tant que les membres
          n'ont pas fourni leur portrait : jamais de visage inventé sur un vrai nom. */}
      <section className="bg-[#F4F0EA] border-y border-black/5">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-center text-ink mb-3">Ils gardent leur voix, ils gagnent du temps</h2>
          <p className="text-center text-sm text-slate-500 mb-14">Premiers retours du groupe pilote {COMPANY}.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="testimonials-grid">
            {TESTIMONIALS.map((t) => (
              <figure key={t.n} className="flex flex-col rounded-2xl bg-white border border-black/5 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <Quote className="h-5 w-5 text-coral/40 mb-3 shrink-0" />
                <blockquote className="text-[15px] text-slate-700 leading-relaxed flex-1">{t.q}</blockquote>
                <figcaption className="flex items-center gap-3 mt-5 pt-5 border-t border-slate-100">
                  <span className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-sm font-bold text-white"
                    style={{ backgroundColor: t.c }} aria-hidden="true">{initials(t.n)}</span>
                  <span>
                    <span className="block text-sm font-bold text-ink">{t.n}</span>
                    <span className="block text-xs text-slate-500">{t.r}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="tarifs" className="max-w-6xl mx-auto px-6 py-24">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mb-2">Des tarifs simples, sans surprise</h2>
        <p className="text-center text-sm text-slate-500 mb-2">Tarifs affichés <strong>hors taxes (HT)</strong>, la TVA est ajoutée au paiement.</p>
        <p className="text-center text-sm text-slate-600 max-w-2xl mx-auto mb-8">
          <strong className="text-ink">L'écriture est illimitée, les visuels sont comptés.</strong>{" "}
          Écrivez, affinez, recommencez autant que vous voulez. Ce que mesurent les formules, ce sont les
          affiches et portraits générés par l'IA : la partie qui demande vraiment du calcul.
        </p>
        <div className="flex items-center justify-center gap-3 mb-10">
          <span className={`text-sm font-semibold ${!yearly ? "text-ink" : "text-slate-400"}`}>Mensuel</span>
          <button data-testid="pricing-toggle" onClick={() => setYearly((v) => !v)} className="relative w-12 h-6 rounded-full bg-coral-strong">
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${yearly ? "left-6" : "left-0.5"}`} />
          </button>
          <span className={`text-sm font-semibold ${yearly ? "text-ink" : "text-slate-400"}`}>Annuel <span className="text-coral-dark">(2 mois offerts)</span></span>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mb-6" data-testid="pricing-encarts">
          <button onClick={() => navigate("/invitation")} data-testid="encart-invite" className="text-left rounded-2xl border border-coral/30 bg-coral-soft/50 p-5 hover:border-coral transition-colors">
            <p className="font-serif text-lg text-ink">Invité d'un groupe APOLLO ?</p>
            <p className="text-sm text-slate-600 mt-1">3 jours offerts avec votre code de réunion, puis 14 jours d'essai. <span className="text-coral-dark font-semibold">Activer mon code →</span></p>
          </button>
          <button onClick={() => navigate("/affiliation")} data-testid="encart-affiliate" className="text-left rounded-2xl border border-black/10 bg-white p-5 hover:border-coral transition-colors">
            <p className="font-serif text-lg text-ink">Recommander Pulse</p>
            <p className="text-sm text-slate-600 mt-1">Créez un compte affilié gratuit et gagnez une commission sur vos filleuls. <span className="text-coral-dark font-semibold">Devenir affilié →</span></p>
          </button>
        </div>
        {/* Accès offerts : une ligne discrète. Ils ne doivent pas dominer les formules payantes. */}
        {plans.some((p) => p.price_ht === 0) && (
          <div className="grid sm:grid-cols-2 gap-4 mb-8" data-testid="free-plans">
            {plans.filter((p) => p.price_ht === 0).map((p) => (
              <div key={p.key} className="flex items-start gap-3 rounded-2xl border border-black/5 bg-white/70 px-5 py-4">
                <Check className="h-4 w-4 text-coral-dark shrink-0 mt-1" />
                <div>
                  <p className="text-sm font-bold text-ink">{p.name} <span className="font-normal text-slate-400">· accès offert</span></p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    {p.tagline} {p.features?.[1] ? <span className="text-slate-600">{p.features[1]}.</span> : null}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="grid md:grid-cols-3 gap-5" data-testid="public-plans">
          {plans.filter((p) => p.price_ht !== 0).map((p) => {
            const highlight = !!p.highlight;
            const paid = p.cta === "buy" && p.price_ht > 0;
            return (
              <div key={p.key} className={`relative flex flex-col rounded-2xl border p-6 bg-white ${highlight ? "border-coral ring-1 ring-coral/30 shadow-lg" : "border-black/5"}`}>
                {highlight && <span className="absolute -top-2.5 left-6 text-[10px] font-bold uppercase bg-coral-strong text-white px-2 py-0.5 rounded-full">Recommandé</span>}
                <h3 className="font-serif text-2xl text-ink">{p.name}</h3>
                <p className="text-xs text-slate-500 mt-1 mb-4 min-h-[32px]">{p.tagline}</p>
                <div className="mb-4">
                  {paid ? (
                    <p className="flex items-baseline gap-1"><span className="font-serif text-3xl text-ink">{yearly ? `${fmt(p.price_ht_year)} €` : `${fmt(p.price_ht)} €`}</span><span className="text-sm text-slate-400">HT / {yearly ? "an" : "mois"}</span></p>
                  ) : p.price_ht === 0 ? <p className="font-serif text-3xl text-ink">Gratuit</p> : <p className="font-serif text-2xl text-slate-400">Sur devis</p>}
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {p.features.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-coral-dark shrink-0 mt-0.5" /> {f}</li>)}
                </ul>
                {p.key === "team" ? (
                  <span className="text-center text-xs text-slate-400 py-2.5 border border-slate-200 rounded-full">Bientôt</span>
                ) : (
                  <button data-testid={`public-plan-${p.key}`} onClick={start} className={`w-full py-2.5 rounded-full text-sm font-bold transition-all active:scale-95 ${highlight ? "bg-coral-strong text-white hover:bg-coral-hover" : "border border-coral text-coral-dark hover:bg-coral-strong hover:text-white"}`}>
                    {paid ? "Essayer gratuitement" : "Commencer"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-center text-sm text-slate-500 mt-8">
          Membre {COMPANY} ? Votre accès est offert avec votre adhésion : <button onClick={() => navigate("/login?register=1&source=apollo")} className="text-coral-dark font-semibold hover:underline">inscrivez-vous avec l'adresse e-mail de votre compte {COMPANY}</button>.
        </p>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-24">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mb-12">Questions fréquentes</h2>
        <div className="space-y-3" data-testid="faq-list">
          {FAQ.map((f, i) => (
            <div key={i} className="rounded-2xl border border-black/5 bg-white overflow-hidden">
              <button data-testid={`faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
                <span className="font-semibold text-ink text-sm">{f.q}</span>
                <ChevronDown className={`h-4 w-4 text-coral-dark shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && <p className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{f.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-6 pb-24">
        <div className="rounded-3xl bg-coral-strong text-white text-center p-12 md:p-16">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">Votre prochain post est déjà presque écrit.</h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">Lancez votre essai gratuit de {trialDays} jours, sans carte bancaire.</p>
          <button data-testid="final-cta-btn" onClick={start} className="inline-flex items-center gap-2 text-base font-bold text-coral-dark bg-white px-7 py-3.5 rounded-full hover:bg-coral-soft active:scale-95 transition-all">
            Essayer gratuitement <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
